import { css, cx } from "@emotion/css";
import React, { useContext } from "react";
import { mergeProps } from "react-aria";
import { Tab as AriaTab, TabProps } from "react-aria-components";

import { getTextStyle } from "../../../foundations/typography/styles";
import { NeutralBadge } from "../../badge/NeutralBadge";
import { TabBadgesContext } from "../context";

interface Props extends TabProps {
  badge?: React.ReactNode;
  testID: string;
}

export function Tab(props: Props) {
  const { badges } = useContext(TabBadgesContext);

  const badge = props.id && badges[props.id] ? badges[props.id] : props.badge;

  return (
    <AriaTab
      {...mergeProps(props, {
        className: cx("react-aria-Tab", styles.tab),
      })}
      data-testid={props.testID}
    >
      {(tabRenderProps) => {
        return (
          <>
            {typeof props.children === "function"
              ? props.children(tabRenderProps)
              : props.children}
            {typeof badge === "string" || typeof badge === "number" ? (
              <NeutralBadge type="subtle">{badge}</NeutralBadge>
            ) : (
              badge
            )}
          </>
        );
      }}
    </AriaTab>
  );
}

const styles = {
  tab: css(
    {
      textDecoration: "none",
      alignItems: "center",
      display: "flex",
      gap: "var(--space-1)",
      outline: "none",
      paddingBottom: "var(--space-3)",
      paddingLeft: "var(--space-4)",
      paddingRight: "var(--space-4)",
      paddingTop: "var(--space-3)",
      color: "var(--text-weak)",
      borderColor: "var(--border-base)",
      "&[data-hovered]": {
        color: "var(--text-base)",
        borderColor: "var(--border-input-hover)",
      },
      "&[data-selected]": {
        zIndex: 1,
        color: "var(--text-base)",
        borderColor: "var(--border-input-active)",
      },
      "&[data-disabled]": {
        color: "var(--text-disabled)",
        borderColor: "var(--border-input-disabled)",
        cursor: "not-allowed",
      },
      "&[data-focus-visible]": {
        boxShadow: "var(--focus-input)",
      },
    },
    getTextStyle("global/button-medium")
  ),
};
